<template>
  <div class="py-16 bg-gray-50 min-h-[calc(100vh-200px)]">
    <div class="container mx-auto px-4">
      <h2 class="text-3xl font-bold text-center text-gray-800 mb-2">
        Acompanhamento da Votação
      </h2>
      <div class="text-lg text-center text-gray-600 mb-8">
        Acompanhe em tempo real a participação dos eleitores por Regional.
        <br />
        <span class="text-sm font-semibold text-lime-600">
          <i class="fa fa-refresh animate-spin mr-1" v-if="loading"></i>
          Última atualização: {{ ultimaAtualizacao }}
        </span>
      </div>

      <!-- Card Geral -->
      <div class="flex justify-center mb-10">
        <div
          class="
            w-full
            max-w-2xl
            bg-white
            shadow-md
            rounded-lg
            p-6
            text-center
            border-t-4 border-lime-600
          "
        >
          <h3 class="text-xl font-bold text-gray-700 mb-4">
            PARTICIPAÇÃO GERAL
          </h3>
          <div class="text-3xl font-black text-gray-800 mb-2">
            {{ totalVotantesGeral.toLocaleString("pt-BR") }} /
            {{ totalEleitoresGeral.toLocaleString("pt-BR") }}
          </div>

          <div
            class="
              w-full
              bg-gray-200
              rounded-full
              h-6
              mb-4
              overflow-hidden
              relative
            "
          >
            <div
              class="bg-lime-500 h-6 transition-all duration-1000 ease-in-out"
              :style="{ width: percentualGeral + '%' }"
            ></div>
            <div
              class="
                absolute
                inset-0
                flex
                items-center
                justify-center
                text-xs
                font-bold
                text-gray-800
                drop-shadow-md
              "
            >
              {{ percentualGeral }}%
            </div>
          </div>

          <div
            class="
              flex
              justify-between
              text-sm text-gray-600
              font-semibold
              px-4
            "
          >
            <div class="text-lime-700">
              <i class="fa fa-check-circle mr-1"></i> Já votaram:
              {{ totalVotantesGeral.toLocaleString("pt-BR") }}
            </div>
            <div class="text-amber-600">
              <i class="fa fa-clock-o mr-1"></i> Faltam votar:
              {{
                (totalEleitoresGeral - totalVotantesGeral).toLocaleString(
                  "pt-BR"
                )
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- Regionais (Barras) -->
      <div class="bg-white shadow-md rounded-lg p-6">
        <h3 class="text-xl font-bold text-gray-700 mb-6 border-b pb-2">
          Participação por Regional
        </h3>

        <div
          v-if="regionais.length === 0 && !loading"
          class="text-center text-gray-500 py-8"
        >
          Nenhum dado disponível no momento.
        </div>

        <div v-for="reg in regionais" :key="reg.regionalId" class="mb-5">
          <div class="flex justify-between items-end mb-1">
            <div class="font-bold text-gray-700">
              {{ reg.nome }}
              <span class="text-xs text-gray-500 font-normal ml-1"
                >({{ reg.sigla }})</span
              >
            </div>
            <div class="text-sm font-semibold">
              {{ reg.percentual }}%
              <span class="text-xs text-gray-500 font-normal"
                >({{ reg.totalVotantes.toLocaleString("pt-BR") }}/{{
                  reg.totalEleitores.toLocaleString("pt-BR")
                }})</span
              >
            </div>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              class="h-4 transition-all duration-1000 ease-in-out"
              :class="getBarColor(reg.percentual)"
              :style="{ width: reg.percentual + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Api from "@/api";
import { mapGetters } from "vuex";

export default Vue.extend({
  name: "AcompanhamentoView",
  data() {
    return {
      regionais: [],
      loading: false,
      ultimaAtualizacao: "--:--:--",
      intervalId: null,
    };
  },
  computed: {
    ...mapGetters("eleicaonet", ["getInfo"]),
    totalEleitoresGeral() {
      return this.regionais.reduce((acc, curr) => acc + curr.totalEleitores, 0);
    },
    totalVotantesGeral() {
      return this.regionais.reduce((acc, curr) => acc + curr.totalVotantes, 0);
    },
    percentualGeral() {
      if (this.totalEleitoresGeral === 0) return 0;
      return (
        (this.totalVotantesGeral / this.totalEleitoresGeral) *
        100
      ).toFixed(2);
    },
  },
  methods: {
    async carregarDados() {
      this.loading = true;
      try {
        const response = await Api.get("/Eleitores/acompanhamento-regional");
        // Ordena por maior percentual de participação
        this.regionais = response.data.sort(
          (a, b) => Number(b.percentual) - Number(a.percentual)
        );

        const now = new Date();
        this.ultimaAtualizacao = now.toLocaleTimeString("pt-BR");
      } catch (error) {
        console.error("Erro ao carregar acompanhamento", error);
      } finally {
        this.loading = false;
      }
    },
    getBarColor(pctStr) {
      const pct = Number(pctStr);
      if (pct >= 50) return "bg-green-500";
      if (pct >= 30) return "bg-yellow-500";
      return "bg-red-500";
    },
  },
  mounted() {
    this.carregarDados();
    // Refresh a cada 30 segundos
    this.intervalId = setInterval(() => {
      // Só atualiza se a eleição começou
      if (this.getInfo && this.getInfo.jaComecou) {
        this.carregarDados();
      }
    }, 30000);
  },
  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
});
</script>
